import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Clock, MapPin, Plus, ShoppingCart } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GroupOrderPage = () => {
  const { groupOrderId } = useParams();
  const navigate = useNavigate();
  const [groupOrder, setGroupOrder] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  
  // Get language from localStorage or default to Latvian
  const language = localStorage.getItem('language') || localStorage.getItem('selectedLanguage') || 'lv';
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [cardPaymentsEnabled, setCardPaymentsEnabled] = useState(false);

  useEffect(() => {
    axios.get(`${API}/restaurant-status`)
      .then((res) => setCardPaymentsEnabled(res.data?.card_payments_enabled === true))
      .catch(() => {});
  }, []);

  const translations = {
    title: {
      en: "Group Order",
      ru: "Групповой заказ", 
      lv: "Grupas pasūtījums"
    },
    createdBy: {
      en: "Created by",
      ru: "Создано",
      lv: "Izveidoja"
    },
    deliverTo: {
      en: "Deliver to",
      ru: "Доставить по адресу",
      lv: "Piegādāt uz"
    },
    deliveryTime: {
      en: "Delivery time",
      ru: "Время доставки",
      lv: "Piegādes laiks"
    },
    asap: {
      en: "ASAP (30 min - 1 hour)",
      ru: "Как можно скорее (30 мин - 1 час)",
      lv: "ASAP (30 min - 1 stunda)"
    },
    items: {
      en: "Items in order",
      ru: "Товары в заказе",
      lv: "Preces pasūtījumā"
    },
    noItems: {
      en: "No items added yet",
      ru: "Пока ничего не добавлено",
      lv: "Vēl nav pievienotu preču"
    },
    total: {
      en: "Total",
      ru: "Итого",
      lv: "Kopā"
    },
    addItems: {
      en: "Add Items",
      ru: "Добавить товары",
      lv: "Pievienot preces"
    },
    menu: {
      en: "Menu",
      ru: "Меню",
      lv: "Ēdienkarte"
    },
    all: {
      en: "All",
      ru: "Все",
      lv: "Visi"
    },
    addToCart: {
      en: "Add to Order",
      ru: "Добавить в заказ",
      lv: "Pievienot pasūtījumam"
    },
    orderClosed: {
      en: "This group order is closed",
      ru: "Этот групповой заказ закрыт",
      lv: "Šis grupas pasūtījums ir slēgts"
    },
    orderNotFound: {
      en: "Group order not found",
      ru: "Групповой заказ не найден",
      lv: "Grupas pasūtījums nav atrasts"
    },
    loading: {
      en: "Loading...",
      ru: "Загрузка...",
      lv: "Ielādē..."
    },
    checkout: {
      en: "Checkout Group Order",
      ru: "Оплатить групповой заказ",
      lv: "Apmaksāt grupas pasūtījumu"
    },
    paymentMethod: {
      en: "Payment Method",
      ru: "Способ оплаты",
      lv: "Maksājuma veids"
    },
    cash: {
      en: "Cash on Delivery",
      ru: "Наличными при получении",
      lv: "Skaidra nauda piegādē"
    },
    card: {
      en: "Credit/Debit Card",
      ru: "Банковская карта",
      lv: "Bankas karte"
    }
  };

  useEffect(() => {
    loadGroupOrder();
    loadMenuData();
  }, [groupOrderId]);

  const loadGroupOrder = async () => {
    try {
      // Decode the group order ID to handle URL encoding of # symbol
      const decodedId = decodeURIComponent(groupOrderId);
      const response = await axios.get(`${API}/group-orders/${encodeURIComponent(decodedId)}`);
      setGroupOrder(response.data);
    } catch (error) {
      console.error("Error loading group order:", error);
      setError(error.response?.status === 404 ? "not_found" : "error");
    }
  };

  const loadMenuData = async () => {
    try {
      const [categoriesRes, menuRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/menu`)
      ]);
      
      setCategories(categoriesRes.data);
      setMenuItems(menuRes.data);
    } catch (error) {
      console.error("Error loading menu data:", error);
    } finally {
      setLoading(false);
    }
  };

  const addItemToGroup = async (menuItem) => {
    if (!groupOrder || groupOrder.status !== "open") {
      toast.error("This group order is closed");
      return;
    }

    try {
      const orderItem = {
        menu_item_id: menuItem.id,
        name: menuItem[`name_${language}`],
        price: menuItem.price,
        quantity: 1
      };

      // Use the decoded group order ID for the API call
      const decodedId = decodeURIComponent(groupOrderId);
      await axios.post(`${API}/group-orders/${encodeURIComponent(decodedId)}/items`, orderItem);
      
      // Reload group order to show updated items
      await loadGroupOrder();
      
      toast.success(`${menuItem[`name_${language}`]} added to group order!`);
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item. Please try again.");
    }
  };

  const checkoutGroupOrder = async () => {
    if (!groupOrder || groupOrder.items.length === 0) {
      toast.error("No items in group order to checkout");
      return;
    }

    setCheckingOut(true);

    try {
      // Use the decoded group order ID for the API call
      const decodedId = decodeURIComponent(groupOrderId);
      const response = await axios.post(`${API}/group-orders/${encodeURIComponent(decodedId)}/checkout`, {
        payment_method: paymentMethod
      });

      toast.success("Group order checked out successfully!");
      
      // If card payment, redirect to Stripe
      if (paymentMethod === "card") {
        try {
          const checkoutData = {
            origin_url: window.location.origin,
            order_id: response.data.order_id,
            amount: groupOrder.total_amount
          };
          
          const checkoutResponse = await axios.post(`${API}/payments/checkout/session`, checkoutData);
          
          if (checkoutResponse.data.url) {
            window.location.href = checkoutResponse.data.url;
          }
        } catch (error) {
          console.error("Payment error:", error);
          toast.error("Payment processing failed. Please try again.");
        }
      } else {
        // Cash payment - redirect to confirmation
        navigate(`/order-confirmation/${encodeURIComponent(response.data.order_id)}`);
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to checkout. Please try again.");
      setCheckingOut(false);
    }
  };

  const filteredMenuItems = (selectedCategory 
    ? menuItems.filter(item => item.category === selectedCategory)
    : menuItems).filter(item => item.price != null);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{translations.loading[language]}</p>
        </div>
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {translations.orderNotFound[language]}
          </h1>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }

  if (error || !groupOrder) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error loading group order</h1>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">{translations.title[language]}</h1>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-500" />
              <span className="text-gray-600">{translations.createdBy[language]}:</span>
              <span className="font-medium">{groupOrder.creator_name}</span>
            </div>
            
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
              <div>
                <span className="text-gray-600">{translations.deliverTo[language]}:</span>
                <div className="font-medium">{groupOrder.delivery_address}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 md:col-span-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-gray-600">{translations.deliveryTime[language]}:</span>
              <span className="font-medium">
                {groupOrder.delivery_time 
                  ? new Date(groupOrder.delivery_time).toLocaleString() 
                  : translations.asap[language]
                }
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Current Order */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-4">
              <div className="flex items-center gap-2 mb-4">
                <ShoppingCart className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold">{translations.items[language]}</h2>
              </div>
              
              {groupOrder.items && groupOrder.items.length > 0 ? (
                <>
                  <div className="space-y-3 mb-4">
                    {groupOrder.items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                        <div>
                          <div className="font-medium">{item.name}</div>
                          <div className="text-sm text-gray-600">€{item.price} x {item.quantity}</div>
                        </div>
                        <div className="font-bold">€{(item.price * item.quantity).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center text-lg font-bold mb-4">
                      <span>{translations.total[language]}:</span>
                      <span>€{groupOrder.total_amount.toFixed(2)}</span>
                    </div>
                    
                    {/* Checkout section for creator */}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium mb-2">{translations.paymentMethod[language]}</label>
                        <select
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-md"
                        >
                          <option value="cash">{translations.cash[language]}</option>
                          {cardPaymentsEnabled && <option value="card">{translations.card[language]}</option>}
                        </select>
                      </div>
                      
                      <Button
                        onClick={checkoutGroupOrder}
                        disabled={checkingOut || groupOrder.status !== "open"}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                      >
                        {checkingOut ? "Processing..." : translations.checkout[language]}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{translations.noItems[language]}</p>
                </div>
              )}
              
              {groupOrder.status !== "open" && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                  <p className="text-red-800 font-medium">{translations.orderClosed[language]}</p>
                </div>
              )}
            </div>
          </div>

          {/* Menu */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-2xl font-bold mb-6">{translations.menu[language]}</h2>
              
              {/* Category filters */}
              <div className="flex flex-wrap gap-3 mb-8">
                <Button
                  onClick={() => setSelectedCategory(null)}
                  variant={selectedCategory === null ? "default" : "outline"}
                  className={selectedCategory === null ? "bg-red-600 hover:bg-red-700 text-white" : "border-gray-300"}
                >
                  {translations.all[language]}
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.slug)}
                    variant={selectedCategory === category.slug ? "default" : "outline"}
                    className={selectedCategory === category.slug ? "bg-red-600 hover:bg-red-700 text-white" : "border-gray-300"}
                  >
                    {category[`name_${language}`]}
                  </Button>
                ))}
              </div>

              {/* Menu items grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {filteredMenuItems.map((item) => (
                  <div
                    key={item.id}
                    className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="relative h-40">
                      <img
                        src={item.image}
                        alt={item[`name_${language}`]}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 right-3 bg-red-600 text-white px-3 py-1 rounded-full font-bold">
                        €{item.price.toFixed(2)}
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <h3 className="text-lg font-bold mb-2">{item[`name_${language}`]}</h3>
                      <p className="text-sm text-gray-600 mb-4">{item[`description_${language}`]}</p>
                      <Button
                        onClick={() => addItemToGroup(item)}
                        disabled={groupOrder.status !== "open"}
                        className="w-full bg-green-600 hover:bg-green-700 text-white gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        {translations.addToCart[language]}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupOrderPage;